"use client";

import { useEffect, useRef, useState, type RefObject } from "react";
import Link from "next/link";
import { generateKundali } from "@/lib/api/kundali";
import type { KundaliRequest, KundaliResponse } from "@/lib/api/kundali";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import { trackCalculatorUsed } from "@/lib/analytics";

const RASHI_DATA: Record<string, { hi: string; symbol: string; ruler: string; element: string }> = {
  aries: { hi: "मेष", symbol: "♈", ruler: "Mars", element: "Fire" },
  taurus: { hi: "वृषभ", symbol: "♉", ruler: "Venus", element: "Earth" },
  gemini: { hi: "मिथुन", symbol: "♊", ruler: "Mercury", element: "Air" },
  cancer: { hi: "कर्क", symbol: "♋", ruler: "Moon", element: "Water" },
  leo: { hi: "सिंह", symbol: "♌", ruler: "Sun", element: "Fire" },
  virgo: { hi: "कन्या", symbol: "♍", ruler: "Mercury", element: "Earth" },
  libra: { hi: "तुला", symbol: "♎", ruler: "Venus", element: "Air" },
  scorpio: { hi: "वृश्चिक", symbol: "♏", ruler: "Mars", element: "Water" },
  sagittarius: { hi: "धनु", symbol: "♐", ruler: "Jupiter", element: "Fire" },
  capricorn: { hi: "मकर", symbol: "♑", ruler: "Saturn", element: "Earth" },
  aquarius: { hi: "कुंभ", symbol: "♒", ruler: "Saturn", element: "Air" },
  pisces: { hi: "मीन", symbol: "♓", ruler: "Jupiter", element: "Water" },
};
type RashiInfo = (typeof RASHI_DATA)[string];

export default function RashiCalculatorClient({
  defaultLanguage = "en",
}: {
  defaultLanguage?: "en" | "hi";
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<KundaliResponse | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const isHindi = defaultLanguage === "hi";

  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  async function handleSubmit(data: KundaliRequest) {
    setLoading(true);
    setError(null);
    try {
      const response = await generateKundali({ ...data, skip_interpretation: true });
      setResult(response);
      trackCalculatorUsed({ calculator: "rashi-calculator" });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : isHindi ? "कुछ गड़बड़ हुई" : "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  }

  const rashiKey = result?.moon_rashi?.toLowerCase();
  const rashiInfo = rashiKey ? RASHI_DATA[rashiKey] : null;

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <RashiHeader isHindi={isHindi} />

        {/* Form */}
        {!result && (
          <div className="mx-auto max-w-md">
            <BirthDetailsForm
              onSubmit={handleSubmit}
              loading={loading}
              submitLabel={isHindi ? "मेरी राशि बताएं" : "Find My Rashi"}
              error={error ?? undefined}
              language={defaultLanguage}
              analyticsToolName="rashi-calculator"
            />
          </div>
        )}

        {result && rashiInfo && (
          <RashiResult
            result={result}
            rashiInfo={rashiInfo}
            resultRef={resultRef}
            isHindi={isHindi}
            onReset={() => {
              setResult(null);
              setError(null);
            }}
          />
        )}

        <RashiSeoContent />

        {/* Cross Links */}
        <CalculatorCrossLinks currentPage="rashi-calculator" />
      </div>
    </div>
  );
}

function RashiHeader({ isHindi }: { isHindi: boolean }) {
  return (
    <div className="text-center mb-10">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3 text-ish-accent">
        {isHindi ? "राशि गणना" : "Rashi Ganana"}
      </p>
      <h1
        className="text-[28px] sm:text-[36px] font-bold leading-tight"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        {isHindi ? "निःशुल्क राशि कैलकुलेटर" : "Free Rashi Calculator"}
      </h1>
      <p className="mt-3 text-[15px] leading-relaxed max-w-md mx-auto text-ish-text-muted">
        {isHindi
          ? "अपनी जन्म तिथि दर्ज करें और तुरंत अपनी जन्म राशि (चंद्र राशि) जानें। स्वामी ग्रह, तत्व, और व्यक्तित्व गुण पाएं — 100% निःशुल्क।"
          : "Enter your date of birth and find your Janam Rashi (Moon Sign) instantly. Get your ruling planet, element, and personality traits — 100% free."}
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[11px] font-medium text-ish-text-faint">
        <TrustSignal icon="shield" label={isHindi ? "100% सटीक" : "100% Accurate"} />
        <TrustSignal icon="clock" label={isHindi ? "तुरंत परिणाम" : "Instant Results"} />
        <TrustSignal icon="user" label={isHindi ? "100% निःशुल्क" : "100% Free"} />
      </div>
    </div>
  );
}

function TrustSignal({ icon, label }: { icon: "shield" | "clock" | "user"; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        {icon === "shield" && <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />}
        {icon === "clock" && (
          <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </>
        )}
        {icon === "user" && (
          <>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </>
        )}
      </svg>
      {label}
    </span>
  );
}

function RashiResult({
  result,
  rashiInfo,
  resultRef,
  isHindi,
  onReset,
}: {
  result: KundaliResponse;
  rashiInfo: RashiInfo;
  resultRef: RefObject<HTMLDivElement | null>;
  isHindi: boolean;
  onReset: () => void;
}) {
  const moonPada =
    result.planets.find((planet) => planet.planet_id === "moon")?.nakshatra_pada ?? "-";
  return (
    <div ref={resultRef} className="space-y-6">
      <div className="card-sacred p-6 sm:p-8 text-center">
        <span className="text-5xl block mb-3">{rashiInfo.symbol}</span>
        <h2
          className="text-[28px] font-bold"
          style={{
            fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
            color: "var(--ish-dark)",
          }}
        >
          {result.moon_rashi}
        </h2>
        <p className="text-[18px] mt-1 text-ish-text-muted">{rashiInfo.hi}</p>
        <div className="divider-warm my-5" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <ResultFact label="Element" value={rashiInfo.element} />
          <ResultFact label="Ruler" value={rashiInfo.ruler} />
          <ResultFact
            label="Nakshatra"
            value={result.moon_nakshatra.replace(/_/g, " ")}
            className="capitalize"
          />
          <ResultFact label="Nakshatra Pada" value={String(moonPada)} />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          href="/free-kundali"
          className="btn-sacred text-center font-semibold py-3 px-6 rounded-lg text-[14px]"
        >
          {isHindi ? "पूरी कुंडली पढ़ें →" : "Get Full Kundali Reading →"}
        </Link>
        <button
          onClick={onReset}
          className="text-center font-semibold py-3 px-6 rounded-lg text-[14px] transition-all duration-200"
          style={{ color: "var(--ish-text-muted)", border: "1px solid var(--ish-border)" }}
        >
          {isHindi ? "फिर से गणना करें" : "Calculate Again"}
        </button>
      </div>
    </div>
  );
}

function ResultFact({
  label,
  value,
  className = "",
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div>
      <span className="block text-[12px] mb-1 text-ish-text-faint">{label}</span>
      <span className={`font-semibold text-[14px] text-ish-dark ${className}`}>{value}</span>
    </div>
  );
}

function RashiSeoContent() {
  return (
    <div className="mt-12 card-sacred p-6 sm:p-8">
      <h2
        className="text-[20px] font-bold mb-4"
        style={{
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
          color: "var(--ish-dark)",
        }}
      >
        What is Rashi in Vedic Astrology?
      </h2>
      <div className="space-y-3 text-[14px] leading-relaxed text-ish-text-muted">
        <p>
          In Vedic astrology (Jyotish Shastra), your{" "}
          <strong style={{ color: "var(--ish-dark)" }}>Rashi</strong> or Moon sign is determined by
          the zodiac sign in which the Moon was positioned at the time of your birth. Unlike Western
          astrology which focuses on the Sun sign, Vedic astrology considers the Moon sign as the
          primary indicator of your emotional nature, mind, and personality.
        </p>
        <p>
          The Moon moves through all 12 rashis approximately every 27.3 days, spending about 2.25
          days in each sign. This is why knowing your exact birth time helps calculate a more
          accurate rashi.
        </p>
        <p>
          The 12 rashis are: Mesh (Aries), Vrishabh (Taurus), Mithun (Gemini), Kark (Cancer), Singh
          (Leo), Kanya (Virgo), Tula (Libra), Vrishchik (Scorpio), Dhanu (Sagittarius), Makar
          (Capricorn), Kumbh (Aquarius), and Meen (Pisces).
        </p>
      </div>
    </div>
  );
}
